import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { StatCounter } from "@/components/shared/StatCounter";
import { Reveal, RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { CTABand } from "@/components/shared/CTABand";
import { useConsultation } from "@/components/forms/ConsultationDialog";
import { PROCESS, PROCESS_STATS, IMAGES } from "@/data/content";

export default function HowWeWork() {
  const { open } = useConsultation();
  return (
    <>
      <PageHero
        kicker="The Cyclical Engagement Model"
        title="From discovery to ongoing support — a process that safeguards careers."
        subtitle="Six rigorous phases in one continuous relationship. We remove the guesswork, protect your bandwidth, and stay accountable long after the contract is signed."
        image={IMAGES.meeting}
      >
        <Button variant="hero" size="xl" onClick={open}>
          Request a Free ClarityMap <ArrowRight className="h-5 w-5" />
        </Button>
      </PageHero>

      {/* Timeline */}
      <section className="section-x py-20 sm:py-28">
        <div className="container-tmt">
          <div className="relative">
            <div className="absolute left-[27px] top-2 hidden h-[calc(100%-2rem)] w-px bg-gradient-to-b from-primary/60 via-border to-transparent md:block" />
            <div className="space-y-6">
              {PROCESS.map((p, i) => (
                <Reveal key={p.step} delay={i * 0.05}>
                  <div className="group relative grid gap-5 rounded-2xl border border-border bg-card p-6 transition-colors duration-300 hover:border-primary/40 md:grid-cols-[auto_1fr] md:p-7">
                    <div className="flex items-center gap-4 md:flex-col md:items-center">
                      <span className="relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/30 bg-background font-display text-xl font-semibold text-primary">
                        {p.step}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-semibold sm:text-2xl">{p.title}</h3>
                      <p className="mt-2 max-w-2xl text-muted-foreground">{p.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Process stats */}
          <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-3">
            {PROCESS_STATS.map((s) => (
              <div key={s.label} className="bg-card p-8 text-center">
                <StatCounter value={s.value} label={s.label} className="[&>div:first-child]:mx-auto" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantee strip */}
      <section className="section-x pb-8">
        <div className="container-tmt">
          <Reveal>
            <div className="rounded-2xl border border-primary/20 bg-gradient-surface p-8 sm:p-10">
              <SectionHeading
                kicker="What You Can Count On"
                title="We do not disappear at signature."
                subtitle="Phase 6 is where most firms clock out. It's where we get to work."
              />
              <RevealStagger className="mt-8 grid gap-4 sm:grid-cols-2">
                {[
                  "Quarterly business reviews to keep suppliers honest",
                  "Escalation support when SLAs slip",
                  "Benchmarking refreshed against a moving market",
                  "A single point of accountability for the life of the contract",
                ].map((t) => (
                  <RevealItem key={t}>
                    <div className="flex items-start gap-3 rounded-xl border border-border bg-card/60 px-5 py-4">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                      <span className="text-sm text-foreground/90">{t}</span>
                    </div>
                  </RevealItem>
                ))}
              </RevealStagger>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand title="Ready to see the market without the marketing?" subtitle="Request a free, independent ClarityMap and get a benchmarked read in about 48 hours." />
    </>
  );
}
